import {
  GuidanceApi,
  type GuidanceProgressDto,
  type UpdateGuidanceProgressDto,
} from '@up2stay/up2promo-api-client';

import { useAuthStore } from '@/core/store/auth.store';

/**
 * Shell boot reads Guidance Progress from several plain modules (plan gate,
 * Get started checklist, TourKit storage) within a few seconds; each used to
 * send its own GET (DEV-555). One response per user × scope is shared for a
 * short window, and every write through `updateGuidanceProgress` drops it,
 * so a read after a PATCH always goes to the server.
 */
export const GUIDANCE_PROGRESS_SHARE_MS = 10_000;

type Entry = {
  promise: Promise<GuidanceProgressDto | undefined>;
  at: number;
};

const entries = new Map<string, Entry>();

function scopeKey(workspaceId: string | null | undefined): string {
  const userId = useAuthStore.getState().user?.id ?? 'anonymous';
  return `${userId}:${workspaceId ?? 'user'}`;
}

/** User scope without `workspaceId`, user × workspace scope with it. */
export function getGuidanceProgress(
  workspaceId?: string | null,
): Promise<GuidanceProgressDto | undefined> {
  const key = scopeKey(workspaceId);
  const hit = entries.get(key);
  if (hit && Date.now() - hit.at < GUIDANCE_PROGRESS_SHARE_MS) {
    return hit.promise;
  }

  const promise = GuidanceApi.get(
    workspaceId ? { query: { workspaceId } } : undefined,
  ).then((response) => response.data);
  entries.set(key, { promise, at: Date.now() });
  // A failed read is never shared: the next caller retries.
  promise.catch(() => {
    if (entries.get(key)?.promise === promise) entries.delete(key);
  });
  return promise;
}

export async function updateGuidanceProgress(body: UpdateGuidanceProgressDto) {
  const key = scopeKey(body.workspaceId);
  entries.delete(key);
  try {
    return await GuidanceApi.update({ body });
  } finally {
    // A read that started while the PATCH was in flight may hold old data.
    entries.delete(key);
  }
}

/** Test helper. */
export function resetGuidanceProgressShare(): void {
  entries.clear();
}
